/**
 * DADDL disclosure select — used by snippets/daddl-select.liquid
 * (localization, contact). Options are `.daddl-select__option` + [data-value].
 *
 * The menu is an overlay, never in-flow: on open it is parked on document.body
 * and positioned with the trigger (fixed). That keeps hamburger / overflow /
 * filter ancestors from clipping it or pushing Currency / Save down.
 *
 * Clicks use capture + stopImmediatePropagation so Dawn MenuDrawer does not
 * treat these buttons as hamburger-close controls. Pointer opens do not focus
 * an option (that would paint Dawn's global focus glow).
 */
class DaddlSelect extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.trigger = this.querySelector('[data-daddl-select-trigger]');
    this.list = this.querySelector('[data-daddl-select-list]');
    this.valueEl = this.querySelector('[data-daddl-select-value]');
    this.prefixEl = this.querySelector('[data-daddl-select-prefix]');
    this.input = this.querySelector('[data-daddl-select-input]');
    this.filter = this.querySelector('[data-daddl-select-filter]');
    if (!this.trigger || !this.list) return;

    this.onTriggerClick = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.toggle();
    };
    this.onDocClick = (event) => {
      if (this.contains(event.target) || this.list.contains(event.target)) return;
      this.close();
    };
    this.onKey = (event) => this.handleKey(event);
    this.onReposition = () => this.placeList();

    this.trigger.addEventListener('click', this.onTriggerClick, true);
    this.list.addEventListener(
      'click',
      (event) => {
        const option = event.target.closest('.daddl-select__option');
        if (!option || !this.list.contains(option)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.choose(option);
      },
      true
    );

    if (this.filter) {
      this.filter.addEventListener('click', (event) => event.stopPropagation());
      this.filter.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
        event.stopPropagation();
      });
      this.filter.addEventListener('input', () => this.filterOptions());
    }

    this.syncSelected();
  }

  disconnectedCallback() {
    if (this.onDocClick) document.removeEventListener('click', this.onDocClick, true);
    if (this.onKey) document.removeEventListener('keydown', this.onKey);
    this.unbindPlace();
    this.unparkList();
  }

  // Move the list onto body so overflow/filter ancestors cannot clip it.
  parkList() {
    if (!this.list || this.list.parentElement === document.body) return;
    this.listHome = this.list.parentNode;
    this.listNext = this.list.nextSibling;
    document.body.appendChild(this.list);
    this.list.classList.add('daddl-select__list--portal');
  }

  unparkList() {
    if (!this.list || !this.listHome) return;
    if (this.listNext && this.listNext.parentNode === this.listHome) {
      this.listHome.insertBefore(this.list, this.listNext);
    } else {
      this.listHome.appendChild(this.list);
    }
    this.list.classList.remove('daddl-select__list--portal');
    this.clearPlace();
    this.listHome = null;
    this.listNext = null;
  }

  bindPlace() {
    window.addEventListener('resize', this.onReposition);
    window.addEventListener('scroll', this.onReposition, true);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onReposition);
      window.visualViewport.addEventListener('scroll', this.onReposition);
    }
  }

  unbindPlace() {
    if (!this.onReposition) return;
    window.removeEventListener('resize', this.onReposition);
    window.removeEventListener('scroll', this.onReposition, true);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onReposition);
      window.visualViewport.removeEventListener('scroll', this.onReposition);
    }
  }

  placeList() {
    if (!this.list || !this.trigger || this.list.hidden) return;
    const rect = this.trigger.getBoundingClientRect();
    const gap = 4;
    const pad = 8;
    const preferred = 220;
    const spaceBelow = window.innerHeight - rect.bottom - gap - pad;
    const spaceAbove = rect.top - gap - pad;
    const openUp = spaceBelow < Math.min(preferred, 140) && spaceAbove > spaceBelow;
    const maxH = Math.max(80, Math.min(preferred, openUp ? spaceAbove : spaceBelow));
    const triggerW = Math.round(rect.width);
    const maxW = Math.min(window.innerWidth - pad * 2, 360);

    this.list.style.position = 'fixed';
    this.list.style.zIndex = '80';
    this.list.style.inset = 'auto';
    this.list.style.width = 'max-content';
    this.list.style.minWidth = `${triggerW}px`;
    this.list.style.maxWidth = `${maxW}px`;
    this.list.style.maxHeight = `${Math.round(maxH)}px`;
    this.list.style.right = 'auto';

    const listW = Math.min(Math.max(this.list.getBoundingClientRect().width, triggerW), maxW);
    const rtl = document.documentElement.getAttribute('dir') === 'rtl';
    let left = rtl ? rect.right - listW : rect.left;
    if (left + listW > window.innerWidth - pad) left = window.innerWidth - pad - listW;
    if (left < pad) left = pad;
    this.list.style.left = `${Math.round(left)}px`;

    if (openUp) {
      this.list.style.top = 'auto';
      this.list.style.bottom = `${Math.round(window.innerHeight - rect.top + gap)}px`;
      this.list.dataset.placement = 'top';
    } else {
      this.list.style.top = `${Math.round(rect.bottom + gap)}px`;
      this.list.style.bottom = 'auto';
      this.list.dataset.placement = 'bottom';
    }
  }

  clearPlace() {
    if (!this.list) return;
    this.list.style.position = '';
    this.list.style.zIndex = '';
    this.list.style.inset = '';
    this.list.style.width = '';
    this.list.style.minWidth = '';
    this.list.style.maxWidth = '';
    this.list.style.maxHeight = '';
    this.list.style.left = '';
    this.list.style.right = '';
    this.list.style.top = '';
    this.list.style.bottom = '';
    delete this.list.dataset.placement;
  }

  options() {
    if (!this.list) this.list = this.querySelector('[data-daddl-select-list]');
    if (!this.list) return [];
    return Array.from(this.list.querySelectorAll('.daddl-select__option'));
  }

  get open() {
    return this.trigger.getAttribute('aria-expanded') === 'true';
  }

  toggle() {
    if (this.open) this.close();
    else this.openList();
  }

  openList({ viaKeyboard } = {}) {
    document.querySelectorAll('daddl-select').forEach((el) => {
      if (el !== this && typeof el.close === 'function') el.close();
    });
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.parkList();
    this.list.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.placeList();
    this.bindPlace();
    document.addEventListener('click', this.onDocClick, true);
    document.addEventListener('keydown', this.onKey);
    requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.list.classList.add('is-open');
    });
    // Pointer/touch: do not focus an option — that paints Dawn's focus glow.
    if (!viaKeyboard) return;
    if (this.filter) {
      this.filter.focus();
      return;
    }
    const selected =
      this.list.querySelector('.daddl-select__option[aria-selected="true"]') || this.options()[0];
    selected?.focus();
  }

  close() {
    if (!this.open && !this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    this.list.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.unbindPlace();
    document.removeEventListener('click', this.onDocClick, true);
    document.removeEventListener('keydown', this.onKey);
    if (this.filter) {
      this.filter.value = '';
      this.filterOptions();
    }
    const hide = () => {
      if (this.classList.contains('is-open')) return;
      this.list.hidden = true;
      this.unparkList();
    };
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.hideTimer = setTimeout(hide, reduce ? 0 : 220);
  }

  optionLabel(option) {
    if (option.hasAttribute('data-label')) return option.getAttribute('data-label');
    if (this.hasAttribute('data-value-as-label')) return option.getAttribute('data-value') || '';
    const named = option.querySelector('[data-daddl-select-option-label]');
    if (named) return named.textContent.trim();
    return option.textContent.trim();
  }

  choose(option, { silent } = {}) {
    const value = option.getAttribute('data-value') || '';
    if (this.input) {
      this.input.value = value;
      if (!silent) this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (this.valueEl) this.valueEl.textContent = this.optionLabel(option);

    const prefixSource =
      option.querySelector('[data-daddl-select-option-prefix]') ||
      option.querySelector('.daddl-flag') ||
      option.querySelector('.fi');
    if (this.prefixEl && prefixSource) {
      this.prefixEl.replaceChildren(prefixSource.cloneNode(true));
    }

    this.syncSelected(option);
    this.close();
    if (!silent) this.trigger.focus({ preventScroll: true });
    this.dispatchEvent(
      new CustomEvent('daddl-select:change', { bubbles: true, detail: { value, option } })
    );
  }

  setValue(value) {
    const match = this.options().find((option) => option.getAttribute('data-value') === value);
    if (match) {
      this.choose(match, { silent: true });
      return;
    }
    if (this.input) this.input.value = '';
    if (this.valueEl) {
      this.valueEl.textContent = this.getAttribute('data-placeholder') || '';
    }
    this.syncSelected();
  }

  // Rebuild option buttons (localization swaps languages when Ship to changes).
  replaceOptions(items, preferredValue) {
    if (!this.list) {
      this.list = this.querySelector('[data-daddl-select-list]');
      this.input = this.input || this.querySelector('[data-daddl-select-input]');
      this.valueEl = this.valueEl || this.querySelector('[data-daddl-select-value]');
      this.trigger = this.trigger || this.querySelector('[data-daddl-select-trigger]');
    }
    if (!this.list) return;
    this.close();
    this.options().forEach((option) => option.remove());
    items.forEach((item) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'daddl-select__option';
      button.setAttribute('role', 'option');
      button.dataset.value = item.value;
      button.dataset.label = item.label;
      if (item.lang) button.setAttribute('lang', item.lang);
      Object.entries(item.dataset || {}).forEach(([key, val]) => {
        button.dataset[key] = val;
      });
      const label = document.createElement('span');
      label.setAttribute('data-daddl-select-option-label', '');
      label.textContent = item.label;
      button.appendChild(label);
      this.list.appendChild(button);
    });
    const preferred =
      this.options().find((option) => option.getAttribute('data-value') === preferredValue) ||
      this.options()[0];
    if (preferred) this.choose(preferred, { silent: true });
  }

  syncSelected(active) {
    const current = this.input ? this.input.value : '';
    this.options().forEach((option) => {
      const selected = active ? option === active : option.getAttribute('data-value') === current;
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
  }

  filterOptions() {
    if (!this.filter) return;
    const query = this.filter.value
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();

    this.options().forEach((option) => {
      const haystack = `${option.getAttribute('data-label') || ''} ${option.textContent}`
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();
      option.hidden = query.length > 0 && !haystack.includes(query);
    });
  }

  handleKey(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.trigger.focus();
      return;
    }
    if (!this.open || event.target === this.filter) return;
    const visible = this.options().filter((option) => !option.hidden);
    const index = visible.indexOf(document.activeElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      visible[Math.min(index + 1, visible.length - 1)]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      visible[Math.max(index - 1, 0)]?.focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      visible[0]?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      visible[visible.length - 1]?.focus();
    } else if (event.key === 'Enter' || event.key === ' ') {
      const option = document.activeElement?.closest?.('.daddl-select__option');
      if (option) {
        event.preventDefault();
        this.choose(option);
      }
    }
  }
}

if (!customElements.get('daddl-select')) {
  customElements.define('daddl-select', DaddlSelect);
}
